import { X } from 'lucide-react';
import { HelpTip } from '../../../../components/HelpTip';
import { formatBytes } from '../../../../lib/formatBytes';
import { ROUTING_FILE_MAX_BYTES, fileTooLarge } from '../routingForm';

const ACCEPT = 'application/pdf,.pdf,image/png,.png,image/jpeg,.jpg,.jpeg';

const DESCRIPTION =
  `The soft copy itself, or a scan of the paper — PDF, PNG or JPEG, up to ${formatBytes(ROUTING_FILE_MAX_BYTES)} each. ` +
  "They are filed into the Repository once the document is saved, and each type is verified against the file's own leading bytes rather than its name.";

/**
 * The file box for the files a received or dispatched document carries — SEVERAL, each filed on
 * its own once the record exists.
 *
 * **Laid out like every shared input** — one label line with the description in a `<HelpTip>` and
 * an `sr-only` twin on `aria-describedby`, errors below. The picked files are listed under it with
 * a remove each, because a multiple file input cannot drop one of its own.
 *
 * **The `accept` filter is a convenience, not a gate** — the server verifies the bytes.
 */
export function RoutingFilesInput({
  id,
  files,
  onChange,
  disabled = false,
  error,
}: {
  id: string;
  files: File[];
  onChange: (files: File[]) => void;
  disabled?: boolean;
  error?: string;
}) {
  const oversize = files.filter(fileTooLarge);

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex min-h-5 items-center gap-1.5">
        <label htmlFor={id} className="text-sm text-slate-700">
          Files
        </label>

        <HelpTip label={DESCRIPTION} />
      </div>

      <span id={`${id}-desc`} className="sr-only">
        {DESCRIPTION}
      </span>

      <input
        id={id}
        name={id}
        type="file"
        multiple
        accept={ACCEPT}
        disabled={disabled}
        aria-describedby={`${id}-desc`}
        aria-invalid={oversize.length > 0 || Boolean(error)}
        // Appended, so picking twice adds rather than replaces; the value is cleared so the same
        // file can be picked again after it was removed.
        onChange={(event) => {
          const picked = Array.from(event.target.files ?? []);
          event.target.value = '';
          onChange([...files, ...picked]);
        }}
        className="block w-full rounded-lg border border-slate-300 bg-white text-sm text-slate-600 file:mr-3 file:cursor-pointer file:rounded-l-lg file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm file:text-slate-700 hover:file:bg-slate-200 disabled:cursor-not-allowed disabled:bg-slate-50"
      />

      {files.length > 0 ? (
        <ul className="flex flex-col divide-y divide-slate-200 rounded-lg border border-slate-200">
          {files.map((file, index) => (
            <li key={`${file.name}-${file.size}-${index}`} className="flex items-center gap-3 px-3 py-2">
              <span className="min-w-0 flex-1 truncate text-sm text-slate-600">{file.name}</span>
              <span
                className={`shrink-0 font-mono text-xs tabular-nums ${fileTooLarge(file) ? 'text-red-700' : 'text-slate-500'}`}
              >
                {formatBytes(file.size)}
              </span>
              <button
                type="button"
                id={`${id}-remove-${index}`}
                onClick={() => onChange(files.filter((_, i) => i !== index))}
                disabled={disabled}
                aria-label={`Remove ${file.name}`}
                className="shrink-0 rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {oversize.length > 0 ? (
        <p className="text-xs text-red-700">
          {oversize.length === 1 ? `${oversize[0].name} is` : `${oversize.length} files are`} over the{' '}
          {formatBytes(ROUTING_FILE_MAX_BYTES)} limit. Remove {oversize.length === 1 ? 'it' : 'them'} to continue.
        </p>
      ) : error ? (
        <p className="text-xs text-red-700">{error}</p>
      ) : null}
    </div>
  );
}
